import { canvasToImage } from '@frontend/pitch'
import {
  useImageUrl,
  usePlayerActions,
  usePlayerSessionSummary,
  useReportingTeamSessions,
  useSessionPlayers
} from '@frontend/react'
import { Card, Skeleton } from '@frontend/ui'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { ExportFooter } from '../../components/export-footer'
import { ExportHeader } from '../../components/export-header'
import { PlayerCarousel } from '../../components/player-carousel'
import { PlayerDetailFilterBar } from '../../components/player-detail-filter-bar'
import { useRegisterExport } from '../../lib/export-context'
import { usePlayVisualization } from '../../lib/play-visualization'
import { getFormattedReportingPlayerLabel } from '../../lib/player-utils'
import { useReportingState } from '../../lib/reporting-state'
import { useReportingUnitSystem } from '../../lib/use-reporting-unit-system'
import { PitchCard } from './components/pitch-card'
import { StatsCard } from './components/stats-card'
import {
  buildDrillOptions,
  buildTableData,
  filterByDrill,
  filterByPosition,
  resolveColumns
} from './formatter'
import { detectPresenceKeyDrift } from './hooks/player-availability'
import { usePlayerAvailability } from './hooks/usePlayerAvailability'
import { PlayerPrint } from './index.print'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TABLE_PAGE_SIZE = 8

// Squad-level: nobody is selectable — an empty session, a position filter that
// matches nobody, or a group no player has any data for.
const NO_PLAYERS_MESSAGE = 'No players have data for the selected view.'
// Player-level: a player IS selected (the carousel is populated) but has no data
// for the current view — distinct wording so it never contradicts the carousel.
const NO_DATA_FOR_PLAYER_MESSAGE =
  'This player has no data for the selected view.'
// Shown in the pitch map's own slot when it's empty and the table isn't. The
// two are fed by separate backend pipelines (aggregated metrics vs. the raw
// event tree) and can legitimately diverge. The inverse
// divergence — events but no aggregated stats — hides the table entirely
// instead of showing a message (see tableHasNoData below).
const NO_PITCH_MAP_DATA_MESSAGE =
  'No pitch map data for this player in this session — aggregated stats are available in the table.'

// ---------------------------------------------------------------------------
// PlayerDetail
// ---------------------------------------------------------------------------

export function PlayerDetail() {
  const { playerId: playerIdParam } = useParams<{ playerId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  // --- Reporting context ---
  const {
    teamId,
    sessionId,
    team,
    session,
    sport,
    config: sportConfig,
    isLoading: contextLoading
  } = useReportingState()

  const unitSystem = useReportingUnitSystem(sport)
  const tabGroups = sportConfig.playerPerformance.tabGroups

  // --- Search Param state ---

  const selectedDrillId = searchParams.get('drill')
  const positionFilter = searchParams.get('positionFilter') ?? ''

  // --- Helpers ---
  const updateParam = useCallback(
    (
      key: string,
      value: string | null,
      extras?: Record<string, string | null>
    ) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        if (value == null || value === '') next.delete(key)
        else next.set(key, value)
        if (extras) {
          for (const [k, v] of Object.entries(extras)) {
            if (v == null || v === '') next.delete(k)
            else next.set(k, v)
          }
        }
        return next
      })
    },
    [setSearchParams]
  )

  const setSelectedDrillId = (id: string | null) => updateParam('drill', id)
  const setPositionFilter = (pos: string) => updateParam('positionFilter', pos)

  // Player selection lives in the URL (?player=…) like every other filter, so
  // reload / share / back reflect who's shown. `/player/:playerId` stays a
  // deep-link entry — the effect below seeds the param from it once, after
  // which ?player= is the source of truth.
  const selectedPlayerId = searchParams.get('player') ?? playerIdParam ?? null
  const setSelectedPlayerId = useCallback(
    (id: string | null) => updateParam('player', id),
    [updateParam]
  )

  // --- Data fetching ---
  const { list: sessions, isFetching: sessionsFetching } =
    useReportingTeamSessions(teamId)
  const {
    list: players,
    map: playersMap,
    isFetching: playersFetching
  } = useSessionPlayers(teamId, sessionId)
  const { data: playerSummary, isFetching: summaryFetching } =
    usePlayerSessionSummary(teamId, sessionId, selectedPlayerId || '')
  const { list: actions, isFetching: actionsFetching } = usePlayerActions(
    teamId,
    sessionId,
    selectedPlayerId || ''
  )

  const getImageUrl = useImageUrl()

  // --- Tab State ---

  const urlGroupKey = searchParams.get('group') ?? ''
  const urlTabKey = searchParams.get('tab') ?? ''

  // Per-tab player availability across the whole session, derived from the
  // all-players summary's `metricsPresent` signal. Drives carousel filtering
  // (U3) and tab-strip gating (U4); supersedes the per-player tab-hiding that
  // `useAvailableTabsForPlayer` used to do off the single-player summary.
  const {
    presenceByPlayer,
    hasTabData,
    hasEventData,
    hasSummaryData,
    isFetching: availabilityFetching
  } = usePlayerAvailability(teamId, sessionId)

  // The all-players summary settled with zero players → nobody has data for the
  // session, so no player is selectable. Gate on `isFetching` (not `isLoading`):
  // a session switch refetches already-cached data with `isLoading` false, and
  // we must not declare "no data" — nor flash an empty state — while that
  // request is still in flight.
  const noSessionData = !availabilityFetching && !hasSummaryData

  const activeGroup = useMemo(
    () => tabGroups.find((g) => g.key === urlGroupKey) ?? tabGroups[0],
    [tabGroups, urlGroupKey]
  )

  // --- Side effects ---

  // DEV-only alias-map drift signal: warn when the backend emitted presence keys
  // the FE doesn't map, or when FE tab columns resolve to keys the backend never
  // emitted. Interim guard until the backend ships a presence-key manifest the
  // FE can test against in CI.
  useEffect(() => {
    if (!import.meta.env.DEV) return
    const { unmappedByFe, notEmitted } = detectPresenceKeyDrift(
      presenceByPlayer,
      tabGroups
    )
    if (unmappedByFe.length)
      console.warn(
        '[presence-key drift] backend emitted presence keys with no FE column ' +
          'mapping (BE rename or new metric — rename the FE accessor to the BE key, or add a column):',
        unmappedByFe
      )
    if (notEmitted.length)
      console.warn(
        '[presence-key drift] FE presence-backed columns not emitted this ' +
          'session (legit if unrecorded; recurring → the accessor is likely ' +
          'deferred (add to DEFERRED_ACCESSORS) or misnamed vs the BE key):',
        notEmitted
      )
  }, [presenceByPlayer, tabGroups])

  // Seed ?player= from the /player/:playerId deep-link entry once; after that
  // the search param is canonical (the fallback in `selectedPlayerId` covers
  // the render before this lands).
  useEffect(() => {
    if (playerIdParam && !searchParams.get('player'))
      setSelectedPlayerId(playerIdParam)
  }, [playerIdParam, searchParams, setSelectedPlayerId])

  // Auto-pick the first available session if nothing's selected
  useEffect(() => {
    if (sessions.length > 0 && teamId && !sessionsFetching) {
      const stillValid = sessions.some((s) => s.id === sessionId)
      if (!sessionId || !stillValid) updateParam('session', sessions[0].id)
    }
  }, [sessions, teamId, sessionId, updateParam, sessionsFetching])

  // Register this session for the export context
  useRegisterExport(
    teamId && sessionId
      ? {
          sessionId,
          sessionDate: session?.date ?? '',
          sessionName: session?.name ?? '',
          sport
        }
      : null,
    [teamId, sessionId, session?.date, session?.name, sport]
  )

  // --- Derived data ---
  const activePlayerId = selectedPlayerId || ''

  const player = useMemo(
    () => (activePlayerId && playersMap ? playersMap[activePlayerId] : null),
    [activePlayerId, playersMap]
  )

  // Sort by player number, can be null so check for null to ensure they come first
  const sortedPlayers = useMemo(
    () =>
      [...players].sort((a, b) => {
        if (a.number === null && b.number === null) return 0
        if (a.number === null) return -1
        if (b.number === null) return 1

        return a.number - b.number
      }),
    [players]
  )

  const positionFilteredPlayers = useMemo(
    () => filterByPosition(sortedPlayers, positionFilter, sportConfig),
    [sortedPlayers, positionFilter, sportConfig]
  )

  // The session has selectable players but NONE has data for any tab in the
  // active group — a "dead" carousel. Collapse to the empty state rather than
  // list players you can't get data for. Naturally false when `metricsPresent`
  // is absent / older backend (hasTabData then shows-all), and false when the
  // group has a deferred (show-all) tab (there's still something to show).
  // A player with raw event data (hasEventData) but no aggregated metrics
  // still counts as "has something" here, so a metrics-pipeline gap doesn't
  // hide a whole group of players who have real pitch-map data.
  const noGroupData = useMemo(
    () =>
      positionFilteredPlayers.length > 0 &&
      positionFilteredPlayers.every(
        (p) =>
          activeGroup.tabs.every((tab) => !hasTabData(p.id, tab)) &&
          !hasEventData(p.id)
      ),
    [positionFilteredPlayers, activeGroup.tabs, hasTabData, hasEventData]
  )

  // Player-first tab gating: show STRICTLY the tabs the selected player has
  // metrics for — a tab without data is never offered (it would only render
  // dashes). No show-all fallback: a player with no qualifying tab has the
  // whole table hidden instead (tableHasNoData, below).
  const availableTabs = useMemo(() => {
    if (!activePlayerId) return activeGroup.tabs
    return activeGroup.tabs.filter((tab) => hasTabData(activePlayerId, tab))
  }, [activeGroup.tabs, activePlayerId, hasTabData])

  // Resolve the active tab within the available set; when the URL tab is hidden
  // this falls back to the first available tab (R12). The final fallback keeps
  // activeTab a real tab object while the table is hidden (availableTabs
  // empty) — downstream derivations (columns, auto-select) never see
  // undefined.
  const activeTab = useMemo(
    () =>
      availableTabs.find((t) => t.key === urlTabKey) ??
      availableTabs[0] ??
      activeGroup.tabs[0],
    [availableTabs, urlTabKey, activeGroup.tabs]
  )

  // Carousel lists every position-filtered player (player-first: the tab strip,
  // not the carousel, reflects per-player data availability — so switching tabs
  // never swaps the selection). Empty when the session has no data at all, or
  // when no player has data for the active group (collapse the dead carousel).
  const filteredPlayers = useMemo(
    () => (noSessionData || noGroupData ? [] : positionFilteredPlayers),
    [noSessionData, noGroupData, positionFilteredPlayers]
  )

  // The carousel can be empty even when the session has players — a position
  // filter that matches nobody. Distinguish that (show the empty-state message)
  // from "players still loading" (`sortedPlayers` empty), where the loading
  // skeleton should win.
  const noSelectablePlayer =
    filteredPlayers.length === 0 && sortedPlayers.length > 0

  // Players with nothing to show for the active group are dropped from the
  // carousel display (distinct from noGroupData, which collapses the whole
  // carousel). NOT used for `filteredPlayers` (feeds auto-select eligibility
  // below) — a manually selected or deep-linked no-data player must still
  // resolve and show its own message, not get silently swapped away.
  const carouselPlayers = useMemo(
    () =>
      filteredPlayers.filter(
        (p) =>
          activeGroup.tabs.some((tab) => hasTabData(p.id, tab)) ||
          hasEventData(p.id)
      ),
    [filteredPlayers, activeGroup.tabs, hasTabData, hasEventData]
  )

  const playerOptions = useMemo(
    () =>
      carouselPlayers.map((p) => ({
        value: p.id,
        name: getFormattedReportingPlayerLabel(p)
      })),
    [carouselPlayers]
  )

  // Default to the first available player, and re-resolve to it when the active
  // player drops out of the filtered set (e.g. a position-filter or roster
  // change) so the table is never left blank (R9, R10). Tab changes no longer
  // affect this set, so switching tabs never swaps the selected player. Three
  // preference tiers, most relevant first: (1) has metrics for the active tab,
  // (2) has raw event data (hasEventData) even without metrics — so we don't
  // land on a genuinely-empty player when a better one is right there in the
  // carousel (roster order is otherwise arbitrary w.r.t. who has data),
  // (3) arbitrary first player when nobody has anything at all.
  // A manual selection is respected as-is.
  useEffect(() => {
    if (filteredPlayers.length === 0) return
    const stillVisible = filteredPlayers.some((p) => p.id === selectedPlayerId)
    if (!selectedPlayerId || !stillVisible) {
      const firstWithData =
        filteredPlayers.find((p) => hasTabData(p.id, activeTab)) ??
        filteredPlayers.find((p) => hasEventData(p.id))
      setSelectedPlayerId((firstWithData ?? filteredPlayers[0]).id)
    }
  }, [filteredPlayers, selectedPlayerId, activeTab, hasTabData, hasEventData])

  const positionOptions = useMemo(
    () =>
      sportConfig.positionFilters.map((f) => ({
        value: f.value,
        label: f.label
      })),
    [sportConfig.positionFilters]
  )

  const columns = useMemo(
    () => resolveColumns(tabGroups, activeGroup.key, activeTab.key),
    [tabGroups, activeGroup.key, activeTab.key]
  )

  const tableData = useMemo(
    () => buildTableData(playerSummary, session, sportConfig),
    [playerSummary, session, sportConfig]
  )

  // Table and pitch map are fetched independently and can legitimately
  // disagree. Checked pre-drill-filter/pre-pagination — a drill filter
  // matching no rows is a normal, unrelated empty state.
  const playerDataLoading = summaryFetching || actionsFetching
  // rows.length can't detect emptiness — one scaffold row exists per period
  // even when nothing was aggregated. metricsPresent (plus tab-matching)
  // decides for real; absent (older BE) falls back to row count.
  const tableHasNoData =
    tableData.rows.length === 0 ||
    presenceByPlayer[selectedPlayerId ?? '']?.size === 0 ||
    availableTabs.length === 0
  const pitchMapHasNoData = actions.length === 0

  // Gated on !playerDataLoading: both are legitimately empty mid-fetch,
  // before either request has resolved, and that must show the loading
  // skeleton, not "no data at all".
  const panelHiddenForPlayer =
    !playerDataLoading && tableHasNoData && pitchMapHasNoData

  const pitchMapEmptyMessage = pitchMapHasNoData
    ? NO_PITCH_MAP_DATA_MESSAGE
    : undefined

  const drillOptions = useMemo(() => buildDrillOptions(session), [session])

  const visibleRows = useMemo(
    () => filterByDrill(tableData.rows, selectedDrillId),
    [tableData.rows, selectedDrillId]
  )

  const filteredActions = useMemo(
    () =>
      selectedDrillId
        ? actions.filter((a) => a.drillId === selectedDrillId)
        : actions,
    [actions, selectedDrillId]
  )

  // --- Pagination ---
  const [page, setPage] = useState(0)
  const pageCount = Math.ceil(visibleRows.length / TABLE_PAGE_SIZE)

  useEffect(() => {
    setPage(0)
  }, [visibleRows.length, activeGroup.key, activeTab.key, activePlayerId])

  // The activeTab resolution above can silently fall back to a different
  // tab than the one requested (the selected player doesn't have it) —
  // without this, the URL can say one tab while a different one renders.
  // Guarded on a non-empty urlTabKey so a fresh load with no tab param
  // (nothing was actually requested) is left alone.
  useEffect(() => {
    if (urlTabKey && activeTab.key !== urlTabKey) {
      updateParam('tab', activeTab.key)
    }
  }, [activeTab.key, urlTabKey, updateParam])

  const paginatedRows = useMemo(() => {
    if (visibleRows.length <= TABLE_PAGE_SIZE) return visibleRows
    return visibleRows.slice(
      page * TABLE_PAGE_SIZE,
      (page + 1) * TABLE_PAGE_SIZE
    )
  }, [visibleRows, page])

  // --- Play visualization ---
  const {
    arrows: allArrows,
    visiblePoints,
    visibleArrows,
    metrics,
    toggleCategory,
    selectElement,
    resetSelection,
    isCategorySelected,
    state: vizState
  } = usePlayVisualization(
    filteredActions,
    sportConfig.playCategories,
    activePlayerId,
    {
      players,
      unitSystem
    }
  )

  // --- Refs ---
  const pitchCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const printImageRef = useRef<HTMLImageElement | null>(null)

  // Capture canvas snapshot for the print layout
  useEffect(() => {
    const timer = setTimeout(() => {
      const url = canvasToImage(pitchCanvasRef.current)
      if (url && printImageRef.current) printImageRef.current.src = url
    }, 100)
    return () => clearTimeout(timer)
  }, [visiblePoints, visibleArrows, vizState.selectedElementId])

  // --- Tab / group handlers ---
  const handleGroupChange = (groupKey: string) => {
    if (!groupKey) return
    const group = tabGroups.find((g) => g.key === groupKey)
    updateParam('group', groupKey, { tab: group?.tabs[0]?.key ?? null })
  }

  const setActiveTabKey = (tabKey: string) => {
    if (!tabKey) return
    updateParam('tab', tabKey)
  }

  // --- Render ---

  if (contextLoading) {
    return (
      <div className='space-y-6 px-4 py-6'>
        <Skeleton className='h-10 w-full' />
        <Skeleton className='h-40 w-full' />
        <Skeleton className='h-80 w-full' />
      </div>
    )
  }

  return (
    <>
      <ExportHeader
        teamName={team?.name ?? ''}
        title='Player Analysis'
        sessionName={session?.name}
        partitionName={
          selectedDrillId
            ? drillOptions.find((d) => d.value === selectedDrillId)?.label
            : undefined
        }
        playerName={
          player
            ? player.displayName || `${player.firstName} ${player.lastName}`
            : undefined
        }
        playerNumber={player?.number}
      />

      <div className='flex flex-col gap-6 px-4 py-6 xl:h-[calc(100dvh-65px)] xl:overflow-hidden print:hidden'>
        <PlayerDetailFilterBar
          playerOptions={playerOptions}
          activePlayerId={activePlayerId}
          onPlayerChange={(id) => setSelectedPlayerId(id)}
          drillOptions={drillOptions}
          selectedDrillId={selectedDrillId}
          onDrillChange={setSelectedDrillId}
          positionFilter={positionFilter}
          positionOptions={positionOptions}
          onPositionChange={setPositionFilter}
        />

        {/* Hide the picker entirely when nobody is selectable (empty session,
            position filter matches nobody, or a group no player has data for) —
            the stats panel owns the single empty message, so we don't render an
            empty strip or a duplicate sentence. While fetching, keep it so the
            skeleton shows. Uses carouselPlayers (not filteredPlayers): players
            with neither metrics nor event data for this group are dropped from
            the list entirely, not shown as a dead end. */}
        {carouselPlayers.length > 0 ||
        availabilityFetching ||
        playersFetching ? (
          <PlayerCarousel
            players={carouselPlayers}
            selectedId={activePlayerId || null}
            onSelect={(id) => setSelectedPlayerId(id)}
            loading={availabilityFetching || playersFetching}
          />
        ) : null}

        {/* Order matters once a team+session are chosen: nobody selectable
            (noSessionData / noSelectablePlayer — carousel hidden above) shows the
            squad message; a selected player whose table AND pitch map are BOTH
            empty (panelHiddenForPlayer) shows the single full player-level
            message; if only one of the two is empty, the panel below still
            renders — an empty table is hidden entirely (the pitch map takes the
            full width), an empty pitch map shows its own message next to the
            table; only then fall through to the "select a
            player" prompt for the transient null-selection render. */}
        {!teamId || !sessionId ? (
          <Card className='p-4'>
            <div className='flex h-64 items-center justify-center'>
              <p className='text-muted-foreground'>
                Select a team, session, and player to view stats.
              </p>
            </div>
          </Card>
        ) : noSessionData || noSelectablePlayer ? (
          <Card className='p-4'>
            <div className='flex h-64 items-center justify-center'>
              <p className='text-muted-foreground'>{NO_PLAYERS_MESSAGE}</p>
            </div>
          </Card>
        ) : panelHiddenForPlayer ? (
          <Card className='p-4'>
            <div className='flex h-64 items-center justify-center'>
              <p className='text-muted-foreground'>
                {NO_DATA_FOR_PLAYER_MESSAGE}
              </p>
            </div>
          </Card>
        ) : !player ? (
          <Card className='p-4'>
            <div className='flex h-64 items-center justify-center'>
              <p className='text-muted-foreground'>
                Select a team, session, and player to view stats.
              </p>
            </div>
          </Card>
        ) : playerDataLoading ? (
          <>
            <Skeleton className='h-96 w-full rounded-xl' />
            <Skeleton className='h-[600px] w-full rounded-xl' />
          </>
        ) : (
          <div className='flex w-full flex-col gap-6 xl:h-full xl:min-h-0 xl:flex-1 xl:flex-row xl:items-stretch xl:overflow-hidden'>
            {!tableHasNoData && (
              <StatsCard
                tabGroups={tabGroups}
                activeGroupKey={activeGroup.key}
                onGroupChange={handleGroupChange}
                tabs={availableTabs}
                activeTabKey={activeTab.key}
                onTabChange={setActiveTabKey}
                session={session}
                periodLabel={sportConfig.periodLabel}
                columns={columns}
                rows={paginatedRows}
                totals={selectedDrillId ? null : tableData.totals}
                totalDuration={tableData.totalDuration}
                unitSystem={unitSystem}
                page={page}
                pageCount={pageCount}
                onPageChange={setPage}
              />
            )}

            <PitchCard
              categories={sportConfig.playCategories}
              isCategorySelected={isCategorySelected}
              toggleCategory={toggleCategory}
              metrics={vizState.selectedElementId ? metrics : []}
              points={visiblePoints}
              arrows={visibleArrows}
              allArrows={allArrows}
              emptyMessage={pitchMapEmptyMessage}
              selectedElementId={vizState.selectedElementId}
              canvasRef={pitchCanvasRef}
              onElementClick={(id) => {
                if (id) selectElement(id)
                else resetSelection()
              }}
              pitch={
                session?.pitch
                  ? {
                      sport: sportConfig.pitch.sport,
                      coordinates: session.pitch.pitch
                    }
                  : sportConfig.pitch
              }
              drillRegion={
                selectedDrillId
                  ? session?.drills?.find((d) => d.id === selectedDrillId)
                      ?.region
                  : undefined
              }
              sport={sport}
              exportPlayer={
                player
                  ? {
                      displayName:
                        player.displayName ||
                        `${player.firstName} ${player.lastName}`,
                      number: player.number,
                      position: player.position,
                      image: player.image ? getImageUrl(player.image) : null
                    }
                  : null
              }
              exportSessionName={session?.name ?? ''}
              exportSessionDate={session?.date ?? ''}
              exportDrillName={
                selectedDrillId
                  ? (session?.drills?.find((d) => d.id === selectedDrillId)
                      ?.name ?? 'Full Session')
                  : 'Full Session'
              }
            />
          </div>
        )}
      </div>

      {player && (
        <PlayerPrint
          player={player}
          rows={tableHasNoData ? [] : visibleRows}
          totals={tableHasNoData || selectedDrillId ? null : tableData.totals}
          totalDuration={tableData.totalDuration}
          columns={columns}
          groupLabel={activeGroup.label}
          tabLabel={activeTab.label}
          unitSystem={unitSystem}
          pitchImageRef={printImageRef}
        />
      )}

      <ExportFooter />
    </>
  )
}
